import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/data/products";
import { QtyControl } from "@/components/CartDrawer";
import { MaskLines, EASE } from "@/components/Reveal";

const FIELDS = [
  { name: "name", label: "Full name", required: true, span: 2 },
  { name: "phone", label: "Phone number", required: true, type: "tel" },
  { name: "email", label: "Email (optional)", type: "email" },
  { name: "address", label: "Delivery address", required: true, span: 2 },
  { name: "city", label: "City", required: true },
  { name: "state", label: "State", required: true },
  { name: "pincode", label: "PIN code", required: true },
  { name: "note", label: "Anything we should know? (optional)", span: 2 },
];

const validate = (v) => {
  const e = {};
  FIELDS.forEach((f) => { if (f.required && !v[f.name]?.trim()) e[f.name] = "Required"; });
  if (v.phone && !/^[+\d][\d\s-]{8,14}$/.test(v.phone.trim())) e.phone = "Enter a valid phone number";
  if (v.pincode && !/^\d{6}$/.test(v.pincode.trim())) e.pincode = "6-digit PIN code";
  return e;
};

const Field = ({ f, value, error, onChange }) => (
  <label className={`block ${f.span === 2 ? "sm:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-[0.18em] text-muted">{f.label}</span>
    <input
      data-testid={`checkout-input-${f.name}`}
      type={f.type || "text"}
      value={value || ""}
      onChange={(e) => onChange(f.name, e.target.value)}
      className={`mt-2 w-full border-b bg-transparent py-3 text-lg outline-none transition-colors focus:border-ink ${error ? "border-red-700" : "border-ink/25"}`}
    />
    {error && <span className="mt-1 block text-xs text-red-700" data-testid={`checkout-error-${f.name}`}>{error}</span>}
  </label>
);

const Summary = ({ lines, total }) => (
  <aside className="h-fit rounded-sm bg-sand p-6 md:p-8 lg:sticky lg:top-28" data-testid="order-summary">
    <p className="font-display text-3xl">Your order</p>
    <ul className="mt-6 divide-y divide-ink/10">
      {lines.map((l) => (
        <li key={`${l.slug}-${l.finish}`} className="flex gap-4 py-4">
          <img src={l.finishObj.image} alt={l.product.name} className="h-20 w-16 rounded-sm object-cover" />
          <div className="flex-1">
            <p className="font-display text-xl leading-none">{l.product.name} <span className="text-sm text-muted">{l.product.type}</span></p>
            <p className="mt-1 text-xs text-muted">{l.finishObj.name}</p>
            <div className="mt-2 flex items-center justify-between"><QtyControl line={l} small /><span className="text-sm tabular-nums">{formatINR(l.lineTotal)}</span></div>
          </div>
        </li>
      ))}
    </ul>
    <div className="mt-4 space-y-2 border-t border-ink/15 pt-4 text-sm">
      <div className="flex justify-between"><span className="text-muted">Subtotal</span><span className="tabular-nums">{formatINR(total)}</span></div>
      <div className="flex justify-between"><span className="text-muted">Delivery</span><span>Arranged personally</span></div>
    </div>
    <div className="mt-4 flex items-baseline justify-between border-t border-ink/15 pt-4">
      <span className="text-[11px] uppercase tracking-[0.2em]">Total</span>
      <span className="font-display text-4xl tabular-nums" data-testid="checkout-total">{formatINR(total)}</span>
    </div>
  </aside>
);

const ThankYou = ({ order }) => (
  <main className="grid min-h-screen place-items-center px-5 pt-20 text-center" data-testid="order-confirmation">
    <div className="max-w-xl">
      <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.8, ease: EASE }} className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-clay text-white">
        <Check className="h-7 w-7" />
      </motion.span>
      <h1 className="mt-8 font-display text-6xl leading-[0.9] md:text-8xl"><MaskLines lines={["Thank you,", <em key="n" className="text-clay">{order.name.split(" ")[0]}.</em>]} /></h1>
      <p className="mt-8 text-muted">Your order <span className="text-ink" data-testid="order-reference">{order.ref}</span> for <span className="text-ink">{formatINR(order.total)}</span> has been received. Our team will call you on <span className="text-ink">{order.phone}</span> to confirm details and arrange delivery.</p>
      <Link to="/" data-testid="confirmation-home-button" className="btn-primary mt-10">Continue browsing</Link>
    </div>
  </main>
);

export default function Checkout() {
  const { lines, total, clear } = useCart();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [order, setOrder] = useState(null);
  const onChange = (k, v) => { setValues((s) => ({ ...s, [k]: v })); setErrors((s) => ({ ...s, [k]: undefined })); };
  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setOrder({ ...values, total, ref: `BC-${Date.now().toString().slice(-6)}` });
    clear();
    window.__lenis?.scrollTo(0, { immediate: true });
  };

  if (order) return <ThankYou order={order} />;
  if (!lines.length) {
    return (
      <main className="grid min-h-screen place-items-center px-5 text-center" data-testid="checkout-empty">
        <div><p className="font-display text-5xl italic">Your bag is empty.</p><Link to="/#collection" className="btn-primary mt-8">Explore the collection</Link></div>
      </main>
    );
  }
  return (
    <main className="mx-auto max-w-[1440px] px-5 pb-24 pt-28 md:px-10 md:pt-36">
      <h1 className="font-display text-6xl leading-[0.9] md:text-8xl"><MaskLines lines={["Almost", <em key="h" className="text-clay">home.</em>]} /></h1>
      <div className="mt-14 grid gap-14 lg:grid-cols-[1.3fr_1fr]">
        <form onSubmit={onSubmit} noValidate data-testid="checkout-form">
          <p className="text-[11px] uppercase tracking-[0.28em] text-muted">Delivery details</p>
          <div className="mt-8 grid gap-x-8 gap-y-8 sm:grid-cols-2">
            {FIELDS.map((f) => <Field key={f.name} f={f} value={values[f.name]} error={errors[f.name]} onChange={onChange} />)}
          </div>
          <p className="mt-10 text-sm text-muted">No payment now. We'll personally reach out to confirm your order, finish and delivery date.</p>
          <button type="submit" data-testid="place-order-button" className="btn-primary mt-8 h-14 w-full sm:w-auto sm:px-14">Place order — {formatINR(total)}</button>
        </form>
        <Summary lines={lines} total={total} />
      </div>
    </main>
  );
}
